import type { AlertConfigurationRule } from '@rtkwlf/rendall';
import isEqual from 'lodash/isEqual';
import isPlainObject from 'lodash/isPlainObject';
import mapValues from 'lodash/mapValues';
import omitBy from 'lodash/omitBy';

/** sort and stringify the country and user fields */
function sortAndStringifyFields(entry: Record<string, unknown>) {
  return mapValues(entry, (value, key) => {
    if (key === 'country' || key === 'user') {
      if (Array.isArray(value)) {
        return [...value].sort().join(', ');
      }

      if (typeof value === 'string') {
        return value.split(', ').sort().join(', ');
      }
    }

    return value;
  });
}

function isPrimitive(value: unknown): boolean {
  return (
    value == null || (typeof value !== 'object' && typeof value !== 'function')
  );
}

/** Compare primitive value with an object.
 *  If the object has only one key that has a value which is different than undefined,
 *  compares the primitive value with that key's value
 * @example
 * comparePrimitiveWithObject('CA', {country: 'CA', start: undefined}); // true
 */
function comparePrimitiveWithObject(
  primitiveValue: unknown,
  obj: Record<string, unknown>,
): boolean {
  const filteredObj = omitBy(obj, (value) => value === undefined);
  const keysWithValue = Object.keys(filteredObj);

  if (keysWithValue.length === 1) {
    const objValue = obj[keysWithValue[0]];
    return primitiveValue === objValue;
  }

  return false;
}

function areIdenticalEntries(ruleEntry: unknown, newEntry: unknown): boolean {
  // if ruleEntry is a primitive value and newEntry has only one key that has a value which is different than undefined
  // e.g. newEntry = {country: "CA", start: undefined, expiry: undefined}
  //      ruleEntry = "CA"
  //      - They should be considered duplicate
  if (isPrimitive(ruleEntry) && isPlainObject(newEntry)) {
    return comparePrimitiveWithObject(
      ruleEntry,
      newEntry as Record<string, unknown>,
    );
  }

  if (isPrimitive(newEntry) && isPlainObject(ruleEntry)) {
    return comparePrimitiveWithObject(
      newEntry,
      ruleEntry as Record<string, unknown>,
    );
  }

  // if both entries are objects, remove keys with undefined values and then compare
  // e.g. newEntry = { country: ['IT', 'ES'], user: ['test'], start: undefined };
  //      ruleEntry = { country: ['ES', 'IT'], user: 'test' };
  //      - They should be considered duplicate
  if (isPlainObject(newEntry) && isPlainObject(ruleEntry)) {
    const entry1 = omitBy(
      newEntry as Record<string, unknown>,
      (value) => value === undefined,
    );
    const entry2 = omitBy(
      ruleEntry as Record<string, unknown>,
      (value) => value === undefined,
    );

    return isEqual(
      sortAndStringifyFields(entry1),
      sortAndStringifyFields(entry2),
    );
  }

  return isEqual(newEntry, ruleEntry);
}

export function hasIdenticalAlertEntry(
  alertData: AlertConfigurationRule['data'],
  newEntry: unknown,
) {
  return alertData.some((ruleEntry: unknown) =>
    areIdenticalEntries(ruleEntry, newEntry),
  );
}
